/**
 * Shared utility functions for building Cortex context messages
 * Used by both useRegisterCortexContext and cortex-drawer
 */

/**
 * Builds a context message from contextData
 * Extracts text content from contextData using the 'content' key, or creates a summary
 */
export function buildContextMessage(contextData: Record<string, any>): string {
  // Use generic 'content' key for text content
  if (contextData.content && typeof contextData.content === "string") {
    return contextData.content;
  }

  // If no 'content' field found, serialize the entire context data
  // This preserves arrays and objects so AI can actually use the data
  try {
    // Track visited objects to prevent circular references
    const visited = new WeakSet();
    const MAX_DEPTH = 10; // Prevent deeply nested structures
    const MAX_ARRAY_LENGTH = 50; // Limit array size

    // Recursive function to sanitize data
    const sanitizeValue = (value: any, depth: number = 0): any => {
      // Prevent infinite recursion
      if (depth > MAX_DEPTH) {
        return "[Max depth reached]";
      }

      // Handle null/undefined
      if (value === null || value === undefined) {
        return value;
      }

      // Handle primitives
      if (
        typeof value !== "object" ||
        value instanceof Date ||
        value instanceof RegExp
      ) {
        return value;
      }

      // Handle circular references
      if (visited.has(value)) {
        return "[Circular reference]";
      }

      // Handle arrays
      if (Array.isArray(value)) {
        if (value.length === 0) {
          return [];
        }

        visited.add(value);
        try {
          if (value.length <= MAX_ARRAY_LENGTH) {
            return value.map(item => sanitizeValue(item, depth + 1));
          } else {
            // Include first items + summary
            return [
              ...value
                .slice(0, MAX_ARRAY_LENGTH)
                .map(item => sanitizeValue(item, depth + 1)),
              `... and ${value.length - MAX_ARRAY_LENGTH} more items`,
            ];
          }
        } finally {
          visited.delete(value);
        }
      }

      // Handle objects
      visited.add(value);
      try {
        const sanitized: Record<string, any> = {};
        let keyCount = 0;
        const MAX_KEYS = 100; // Limit object keys to prevent huge objects

        for (const [key, val] of Object.entries(value)) {
          if (keyCount >= MAX_KEYS) {
            sanitized["..."] =
              `... and ${Object.keys(value).length - MAX_KEYS} more keys`;
            break;
          }
          sanitized[key] = sanitizeValue(val, depth + 1);
          keyCount++;
        }

        return sanitized;
      } finally {
        visited.delete(value);
      }
    };

    // Sanitize the entire context data
    const sanitizedData: Record<string, any> = {};
    for (const [key, value] of Object.entries(contextData)) {
      sanitizedData[key] = sanitizeValue(value);
    }

    // Return as formatted JSON for readability
    return Object.keys(sanitizedData).length > 0
      ? JSON.stringify(sanitizedData, null, 2)
      : "";
  } catch (error) {
    console.error("[Cortex] Error building context message:", error);
    // Fallback: return empty string
    return "";
  }
}

/**
 * Builds conversation history with system message for text-based contexts
 */
export function buildConversationHistoryWithContext(
  conversationHistory: Array<{ role: string; content: string }>,
  contextData: Record<string, any>,
  systemPrompt?: string,
  buildContextMessageFn?: (data: Record<string, any>) => string
): Array<{ role: "user" | "assistant" | "system"; content: string }> {
  // Remove any existing system messages
  let finalHistory = conversationHistory.filter(
    m => m.role !== "system"
  ) as Array<{ role: "user" | "assistant" | "system"; content: string }>;

  const contextBuilder = buildContextMessageFn || buildContextMessage;
  const contextMessage = contextBuilder(contextData);

  // Only add system message if we have actual content
  if (
    (contextMessage && contextMessage.trim()) ||
    (systemPrompt && systemPrompt.trim())
  ) {
    let systemMessageContent = "";
    if (systemPrompt && systemPrompt.trim()) {
      systemMessageContent = systemPrompt;
    }
    if (contextMessage && contextMessage.trim()) {
      systemMessageContent = systemMessageContent
        ? `${systemMessageContent}\n\nContext:\n${contextMessage}`
        : `Context:\n${contextMessage}`;
    }

    // Only add if we have non-empty content
    if (systemMessageContent && systemMessageContent.trim()) {
      finalHistory = [
        { role: "system", content: systemMessageContent },
        ...finalHistory,
      ];
    }
  }

  return finalHistory;
}
